import { Flex, Text, type BoxProps } from '@mantine/core'
import { useMemo } from 'react'

import { type CoreQuotePreviewItem } from '@flecto/types'
import { TextCurrency } from '@flecto/ui'

import { useDocumentState } from '../../hooks/useDocumentState'
import { SectionBookingItem } from './SectionItemsBody'
import { SectionTable } from './SectionTable'

type SectionItemsProps = BoxProps
type ValidLanguage = 'en' | 'es' | 'pt'

export function SectionItems({ ...props }: Readonly<SectionItemsProps>) {
  const { documentPreview, language, translate } = useDocumentState()

  const showTaxes = documentPreview?.owner?.show_prices_with_tax ?? false
  const depositCents = documentPreview?.deposit_cents ?? 0
  const lastColumnSize = showTaxes ? '30%' : '40%'
  const totalItemsWithTaxes =
    documentPreview?.booking?.items_total_with_vat_cents ?? 0
  const totalItemsWithoutTaxes =
    documentPreview?.booking?.items_total_cents ?? 0
  if ((documentPreview?.booking_items ?? []).length <= 0) {
    return null
  }
  const groupedBookingItems = useMemo(() => {
    const bookingItems = documentPreview?.booking_items ?? []

    if (bookingItems.length === 0) return []

    const languageKey = (language as ValidLanguage) || 'en'

    const grouped = bookingItems.reduce<Record<number, CoreQuotePreviewItem[]>>(
      (acc, item) => {
        const categoryId = Number(item.item.category.id)
        ;(acc[categoryId] ||= []).push(item)
        return acc
      },
      {}
    )

    return Object.values(grouped).sort((groupA, groupB) => {
      const categoryA = groupA[0]?.item?.category
      const categoryB = groupB[0]?.item?.category

      if (!categoryA || !categoryB) return 0

      const parentLabelA =
        categoryA.parent?.labels[languageKey]?.toLowerCase() || ''
      const parentLabelB =
        categoryB.parent?.labels[languageKey]?.toLowerCase() || ''

      const categoryLabelA = categoryA.labels[languageKey]?.toLowerCase() || ''
      const categoryLabelB = categoryB.labels[languageKey]?.toLowerCase() || ''

      return (
        parentLabelA.localeCompare(parentLabelB) ||
        categoryLabelA.localeCompare(categoryLabelB)
      )
    })
  }, [documentPreview?.booking_items?.length, language])

  return (
    <SectionTable {...props}>
      <SectionTable.Header>
        <Text w='50%' fw={500} size='md'>
          {translate?.('label_product')}
        </Text>
        <Text w='10%' fw={500} size='md'>
          {translate?.('label_quantity')}
        </Text>
        {showTaxes && (
          <Text w='10%' fw={500} size='md'>
            {translate?.('label_tax_symbol')}
          </Text>
        )}
        <Text w={lastColumnSize} ta='right' fw={500} size='md'>
          {translate?.(
            showTaxes ? 'label_price_with_taxes' : 'label_price_without_taxes'
          )}
        </Text>
      </SectionTable.Header>
      <SectionTable.Body>
        {groupedBookingItems.map((items, index) => {
          const parentCategoryLabel =
            items[0].item.category.parent?.labels?.[
              (language as ValidLanguage) || 'en'
            ]
          const categoryLabel =
            items[0].item.category.labels?.[(language as ValidLanguage) || 'en']

          return (
            <SectionBookingItem
              key={index}
              parentCategoryLabel={parentCategoryLabel}
              categoryLabel={categoryLabel}
              items={items}
              showTaxes={showTaxes}
              lastColumnSize={lastColumnSize}
            />
          )
        })}
      </SectionTable.Body>
      <SectionTable.Footer>
        <Flex gap='xs'>
          <Text fw={500} size='md'>
            {translate?.(
              showTaxes
                ? 'label_subtotal_with_taxes'
                : 'label_subtotal_without_taxes'
            )}
            :
          </Text>
          <TextCurrency
            fw={500}
            cents={showTaxes ? totalItemsWithTaxes : totalItemsWithoutTaxes}
            size='md'
          />
        </Flex>
        {depositCents > 0 && (
          <Flex gap='xs'>
            <Text c='dimmed' size='md'>
              {translate?.('label_deposit')}:
            </Text>
            <TextCurrency cents={depositCents} c='dimmed' size='md' />
          </Flex>
        )}
      </SectionTable.Footer>
    </SectionTable>
  )
}
